import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Check, X, Download } from "lucide-react";
import { ToolShell } from "@/components/ToolShell";
import { buildSeo } from "@/lib/seo";
import { COMMON_USER_AGENTS, testRobots } from "@/lib/robots-parser";

export const Route = createFileRoute("/tools/robots-tester")({
  head: () =>
    buildSeo({
      title: "Robots.txt Tester — Test Googlebot, GPTBot, ClaudeBot & PerplexityBot",
      description:
        "Free robots.txt tester. Paste your robots.txt, a URL, and a user-agent — see whether Googlebot, GPTBot, ChatGPT-User, ClaudeBot, PerplexityBot or any bot is allowed to crawl, and which exact rule decided it.",
      path: "/tools/robots-tester",
      ogEyebrow: "Robots Tester",
      keywords: "robots.txt tester, robots.txt checker, gptbot tester, claudebot tester, perplexitybot tester",
    }),
  component: RobotsTester,
});

const SAMPLE = `User-agent: *
Disallow: /admin/
Disallow: /cart
Allow: /admin/public/

User-agent: GPTBot
Disallow: /

User-agent: PerplexityBot
Disallow: /private/
`;

function RobotsTester() {
  const [robotsTxt, setRobotsTxt] = useState(SAMPLE);
  const [urls, setUrls] = useState("https://example.com/admin/dashboard\nhttps://example.com/admin/public/login\nhttps://example.com/blog/post");
  const [userAgent, setUserAgent] = useState("Googlebot");
  const [customUa, setCustomUa] = useState("");
  const [fetching, setFetching] = useState(false);
  const [fetchErr, setFetchErr] = useState<string | null>(null);

  const ua = userAgent === "__custom__" ? customUa : userAgent;

  const results = useMemo(() => {
    const list = urls.split(/\r?\n/).map((u) => u.trim()).filter(Boolean);
    return list.map((u) => ({ url: u, result: testRobots(robotsTxt, u, ua) }));
  }, [robotsTxt, urls, ua]);

  const fetchLive = async () => {
    const first = urls.split(/\r?\n/).map((u) => u.trim()).find(Boolean);
    if (!first) return;
    setFetching(true);
    setFetchErr(null);
    try {
      const u = new URL(first);
      const res = await fetch(`${u.origin}/robots.txt`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      setRobotsTxt(text);
    } catch (e) {
      setFetchErr(`Couldn't fetch (likely CORS-blocked). Open ${new URL(first).origin}/robots.txt in your browser and paste manually. (${(e as Error).message})`);
    } finally {
      setFetching(false);
    }
  };

  return (
    <ToolShell
      eyebrow="Robots.txt Tester"
      title={<>Test any URL against any <span className="text-gradient">crawler.</span></>}
      subtitle="Paste your robots.txt and a list of URLs. Pick a user-agent — Googlebot, GPTBot, ClaudeBot, PerplexityBot, or any custom bot. See exactly which rule decided each URL."
      toolName="Robots.txt Tester"
      toolPath="/tools/robots-tester"
      description="Free robots.txt tester implementing Google's longest-match rule for Googlebot and AI crawlers (GPTBot, ClaudeBot, PerplexityBot)."
      howToSteps={[
        { name: "Paste your robots.txt", text: "Either paste manually, or click 'Fetch live robots.txt' to pull it from the first URL's origin." },
        { name: "Add URLs to test", text: "One URL per line. The tool tests all of them in bulk against the same user-agent." },
        { name: "Pick a user-agent", text: "Choose Googlebot, GPTBot, PerplexityBot, ClaudeBot, or any of 17+ presets — or paste a custom UA string." },
        { name: "Read the verdict", text: "Each URL shows ALLOWED or BLOCKED, plus the exact rule line and user-agent group that decided it." },
      ]}
      faqs={[
        {
          q: "How does the longest-match rule work?",
          a: "Google evaluates all matching rules in the most-specific user-agent group, then picks the one with the longest pattern. On ties, Allow beats Disallow. This tool implements the same algorithm.",
        },
        {
          q: "Why test against AI bots like GPTBot and ClaudeBot?",
          a: "If you want to be cited by ChatGPT, Claude, or Perplexity, their crawlers must be able to fetch your pages. A common mistake is blocking them inadvertently via 'User-agent: *' rules. This tester catches that.",
        },
        {
          q: "What's the difference between GPTBot, ChatGPT-User, and OAI-SearchBot?",
          a: "GPTBot trains models. ChatGPT-User fetches a page on demand when a ChatGPT user asks about it. OAI-SearchBot powers ChatGPT Search. Block them independently — most sites should allow all three.",
        },
        {
          q: "Why can't 'Fetch live robots.txt' always work?",
          a: "Browser CORS blocks cross-origin fetches to most domains' robots.txt. If it fails, just open https://yoursite.com/robots.txt in a new tab and copy-paste the contents.",
        },
      ]}
      ctaTitle="Need a full technical SEO audit?"
      ctaHref="/services"
      ctaLabel="See services"
    >
      <div className="grid gap-8 lg:grid-cols-2">
        <div className="space-y-5">
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-[0.2em] text-primary">robots.txt</label>
              <button
                type="button"
                onClick={fetchLive}
                disabled={fetching}
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline disabled:opacity-60"
              >
                <Download size={12} /> {fetching ? "Fetching…" : "Fetch live robots.txt"}
              </button>
            </div>
            <textarea
              value={robotsTxt}
              onChange={(e) => setRobotsTxt(e.target.value)}
              rows={14}
              spellCheck={false}
              className="mt-2 w-full rounded-2xl border border-border bg-card px-4 py-3 text-xs font-mono focus:outline-none focus:border-primary/60"
            />
            {fetchErr && <p className="text-xs text-yellow-400 mt-2">{fetchErr}</p>}
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-primary">URLs to test (one per line)</label>
            <textarea
              value={urls}
              onChange={(e) => setUrls(e.target.value)}
              rows={5}
              spellCheck={false}
              className="mt-2 w-full rounded-2xl border border-border bg-card px-4 py-3 text-sm font-mono focus:outline-none focus:border-primary/60"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-primary">User-agent</label>
            <select
              value={userAgent}
              onChange={(e) => setUserAgent(e.target.value)}
              className="mt-2 w-full rounded-2xl border border-border bg-card px-4 py-3 text-sm focus:outline-none focus:border-primary/60"
            >
              {COMMON_USER_AGENTS.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
              <option value="__custom__">Custom…</option>
            </select>
            {userAgent === "__custom__" && (
              <input
                type="text"
                value={customUa}
                onChange={(e) => setCustomUa(e.target.value)}
                placeholder="e.g. MyCustomBot"
                className="mt-2 w-full rounded-2xl border border-border bg-card px-4 py-3 text-sm focus:outline-none focus:border-primary/60"
              />
            )}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Results</div>
          <ul className="space-y-3">
            {results.map(({ url, result }) => (
              <li key={url} className="rounded-2xl border border-border bg-card p-4">
                <div className="flex items-start gap-3">
                  <span
                    className={`mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-full ${
                      result.allowed ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"
                    }`}
                  >
                    {result.allowed ? <Check size={14} /> : <X size={14} />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-[10px] uppercase tracking-[0.18em] font-bold ${
                          result.allowed ? "text-primary" : "text-destructive"
                        }`}
                      >
                        {result.allowed ? "Allowed" : "Blocked"}
                      </span>
                    </div>
                    <div className="text-sm font-mono break-all mt-1">{url}</div>
                    <div className="text-xs text-muted-foreground mt-2">{result.reason}</div>
                  </div>
                </div>
              </li>
            ))}
            {results.length === 0 && <li className="text-sm text-muted-foreground">Add at least one URL above.</li>}
          </ul>
        </div>
      </div>
    </ToolShell>
  );
}